import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { DEPLOY_STATUS } from '../../../../../types/enums/DeployStatus';
import {
  selectLastDeploymentErrors,
  selectLastDeploymentStatus,
  startDeployment,
} from '../../../../../reducers/deploymentsReducer';
import { selectCrmOrgs } from '../../../environments/environmentsReducer';
import { useFunnelMapPage } from '../../useFunnelMapPage';
import { useRequestChangesApiFacade } from '../../../../../apis/facades/useRequestChangesApiFacade';
import { useSweepNotifications } from '../../../../notifications/useSweepNotifications';
import { SweepNotificationVariant } from '../../../../../reducers/notificationsReducer';
import { useSweepRoleGroups } from '../../../../common/useSweepRoleGroups';
import { telemetry } from '../../../../../telemetry';
import { isAxiosError } from 'axios';
import useUserInfo from '../../../../../hooks/useUserInfo';
import { useTimeout } from '../../../../../hooks/useTimeout';

interface UseDeployFunnelParams {
  selectedOrgId: string;
  selectedFunnelId: string;
  note: string;
  funnelMapId: string;
  onDialogClose: () => void;
}

export const useDeployFunnel = ({
  selectedOrgId,
  selectedFunnelId,
  note,
  funnelMapId,
  onDialogClose,
}: UseDeployFunnelParams) => {
  const { setSweepTimeout } = useTimeout();
  const { checkIfUserHasPermissionsForOrg } = useUserInfo();
  const { deployFunnelDetail } = useFunnelMapPage();
  const { createRequestChange } = useRequestChangesApiFacade();
  const { addNotification } = useSweepNotifications();

  const dispatch = useDispatch();
  const deployStatusAsync = useSelector(selectLastDeploymentStatus);
  const deploymentMessages = useSelector(selectLastDeploymentErrors);
  const orgs = useSelector(selectCrmOrgs);

  const [progressDialogOpen, setProgressDialogOpen] = useState(
    deployStatusAsync === DEPLOY_STATUS.IN_PROGRESS,
  );

  const { isUserWithRequestToDeployFunnels } = useSweepRoleGroups();

  const onFailedSaveCallback = useCallback(() => {
    setProgressDialogOpen(false);

    addNotification({
      message: `Funnel save failed`,
      variant: SweepNotificationVariant.Error,
    });
  }, [addNotification]);

  const _saveAndDeploy = useCallback(async () => {
    setProgressDialogOpen(true);
    try {
      await deployFunnelDetail({
        orgId: selectedOrgId,
        funnelId: selectedFunnelId,
        onFailedSaveCallback,
      });
    } catch (e) {
      setProgressDialogOpen(false);
      telemetry.captureError(e);
      let details: string = '';
      if (isAxiosError<{ message?: string; error?: string; messageCode?: number }>(e)) {
        if (e.status !== 409) {
          telemetry.captureError(e);
        }
        details = e.response?.data.message || '';
      }
      addNotification({
        message: `Funnel deploy failed`,
        details: details,
        variant: SweepNotificationVariant.Error,
      });
    }
  }, [addNotification, deployFunnelDetail, onFailedSaveCallback, selectedFunnelId, selectedOrgId]);

  const onConfirmDeployAsync = useCallback(async () => {
    if (!selectedOrgId) return;

    const org = orgs.find((org) => org.id === selectedOrgId);
    if (
      !(await checkIfUserHasPermissionsForOrg(
        selectedOrgId,
        'create:deploy-job',
        `You don't have permission to deploy funnels to environment ${org?.name}`,
      ))
    ) {
      return;
    }

    dispatch(startDeployment());
    setSweepTimeout(_saveAndDeploy, 100);
  }, [
    _saveAndDeploy,
    dispatch,
    selectedOrgId,
    orgs,
    checkIfUserHasPermissionsForOrg,
    setSweepTimeout,
  ]);

  const onRequestChanges = useCallback(async () => {
    if (!selectedOrgId) return;
    onDialogClose();
    try {
      await createRequestChange({
        crmOrgId: selectedOrgId,
        funnelId: selectedFunnelId,
        note,
        funnelMapId,
      });
      addNotification({
        message: 'Your request was sent to your Sweep Admins',
        variant: SweepNotificationVariant.Success,
      });
    } catch (error) {
      addNotification({
        message: 'Ooooops, something went wrong',
        variant: SweepNotificationVariant.Error,
      });
    }
  }, [
    addNotification,
    createRequestChange,
    funnelMapId,
    note,
    selectedFunnelId,
    selectedOrgId,
    onDialogClose,
  ]);

  return {
    progressDialogOpen,
    setProgressDialogOpen,
    deployStatusAsync,
    deploymentMessages,
    orgs,
    onConfirmDeployAsync,
    onRequestChanges,
    isUserWithRequestToDeployFunnels,
  };
};
